// The single-option reads behind "Go together" (together-logic.ts). When the
// options a guest chose come back with no start together on a day of the
// page, the panel asks get_service_availability_bands about each option
// ALONE — at the whole party's count and at its own — to tell "not at the
// same time" from "Full".
//
// Asked only when the panel passes queries (it does so only once the
// combination came back empty on a day of the page), once per page and
// question: the answer is cached like the page read, and belongs to its key
// (a key without an answer yet reads "loading", never a previous answer).
// Any failed read is "error": the panel then keeps "Full" and today's words.

import { useEffect, useRef, useState } from "react";
import type { DayBands } from "./availability-logic";
import type { AloneQuery } from "./together-logic";
import { PAGE_TIMEOUT_MS, fetchBands } from "./useAvailabilityBands";

export type AloneState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  /** Per query key, the page's days as that read answered them. */
  | { status: "ready"; answers: Record<string, Record<string, DayBands>> };

const TTL_MS = 60_000;
const CACHE_MAX = 12;

export function useAloneBands(a: {
  serviceId: string;
  /** Null (or empty) = nothing to ask. */
  queries: AloneQuery[] | null;
  fromYmd: string | null;
  days: number;
  cartSig: string;
}): AloneState {
  const { serviceId, queries, fromYmd, days, cartSig } = a;
  const asking = !!queries && queries.length > 0 && !!fromYmd && days >= 1;
  const qSig = asking ? JSON.stringify(queries!.map((q) => [q.key, q.entry])) : "";
  const key = [serviceId, qSig, fromYmd ?? "", days, cartSig].join("§");

  const [answer, setAnswer] = useState<{ key: string; state: AloneState }>({ key: "", state: { status: "idle" } });
  const cacheRef = useRef(new Map<string, { at: number; answers: Record<string, Record<string, DayBands>> }>());
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  useEffect(() => {
    const qs = queriesRef.current;
    if (!asking || !qs || !fromYmd) {
      setAnswer({ key, state: { status: "idle" } });
      return;
    }
    const cache = cacheRef.current;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) {
      setAnswer({ key, state: { status: "ready", answers: hit.answers } });
      return;
    }
    setAnswer({ key, state: { status: "loading" } });
    const ac = new AbortController();
    let live = true;
    void (async () => {
      const rs = await Promise.all(
        qs.map((q) => fetchBands(serviceId, [q.entry], fromYmd, days, PAGE_TIMEOUT_MS, ac.signal)),
      );
      if (!live) return;
      const answers: Record<string, Record<string, DayBands>> = {};
      for (let i = 0; i < qs.length; i++) {
        const r = rs[i]!;
        if (!r.ok) {
          setAnswer({ key, state: { status: "error" } });
          return;
        }
        answers[qs[i]!.key] = r.days;
      }
      cache.delete(key);
      cache.set(key, { at: Date.now(), answers });
      while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
      setAnswer({ key, state: { status: "ready", answers } });
    })();
    return () => {
      live = false;
      ac.abort();
    };
    // queries travel via queriesRef; `key` carries their signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return answer.key === key ? answer.state : asking ? { status: "loading" } : { status: "idle" };
}
